import get from 'lodash/get';
import angularJwt from 'angular-jwt';
import ngCookies from 'angular-cookies';
import uiRouter from '@uirouter/angularjs';
import moment from 'moment';

import { FullscreenLoaderService } from 'components/fullscreenLoader/fullscreenLoader.service';
import { CheckoutService } from 'services/checkout.service';
import { getSavedCartInfo, setSavedCartInfo } from 'services/cartUtils';

import 'constants/constants.json';
import { Auth0SdkServiceModule } from './auth0sdk.service';

export const AuthService = angular
    .module('checkout.authService', [
        'ngMaterial',
        angularJwt,
        ngCookies,
        uiRouter,
        'app.constants',
        Auth0SdkServiceModule,
        CheckoutService,
        FullscreenLoaderService
    ]).service('authService', (
        $window,
        $state,
        $cookies,
        $location,
        jwtHelper,
        apiService,
        auth0SdkService,
        checkoutService,
        $mdDialog,
        $q,
    ) => {
        const globalCookieOptions = {
            secure: true,
            path: '/'
        };

        function useUniversalLogin() {
            // note, karma tests run under domain 'server'
            return /myvolusion.com|volusionplatformdev.com|server|localhost|127.0.0.1$/.test($location.host());
        }

        function clearUser() {
            clearTokenCookie();
            if ($window.Raven) {
                $window.Raven.setUserContext();
            }
        }

        function login(email, password) {
            clearUser();
            if (useUniversalLogin()) {
                auth0SdkService.loginWithRedirect({
                    redirect_uri: $window.location.href,
                    login_hint: email
                });
            } else {
                return apiService.account.login(email, password).then((response) => {
                    setTokenCookie(response.id_token);
                    return apiService.setShopperId(getShopperIdFromToken(getIdToken()));
                });
            }
        }

        function getShopperIdFromToken(token) {
            return get(jwtHelper.decodeToken(token),
                '["https://api.material.com/user_metadata"].shopperId');
        }

        function getShopperId() {
            if (useUniversalLogin()) {
                return auth0SdkService.getUser().then((user) => {
                    return user && user['https://api.material.com/user_metadata'].shopperId;
                });
            }

            const token = getIdToken();
            if (!token) {
                return $q.resolve();
            }
            return $q.resolve(getShopperIdFromToken(token));
        }

        function logout(useNewCarts) {
            clearUser();
            apiService.setShopperId(null);

            if (useUniversalLogin()) {
                auth0SdkService.logout({
                    returnTo: $window.location.href
                });
            }

            if (!useNewCarts) {
                return checkoutService.getAndUpdateCart((cart) => {
                    delete cart.shippingAddress;
                    delete cart.contactInfo;
                    delete cart.selectedShippingAddressId;
                    return cart;
                }).then(() => {
                    $mdDialog.cancel();
                    return $state.reload();
                });
            }

            setSavedCartInfo({
                ...getSavedCartInfo(),
                shippingAddress: undefined,
                contactInfo: undefined,
                selectedShippingAddressId: undefined
            });

            $mdDialog.cancel();
            return $state.reload();
        }

        function isLoggedIn() {
            if (useUniversalLogin()) {
                return auth0SdkService.isAuthenticated().then((authenticated) => {
                    return authenticated;
                });
            }

            const idTokenCookie = $cookies.get(getIdTokenCookieName());
            return $q.resolve(angular.isDefined(idTokenCookie)
                && !jwtHelper.isTokenExpired(idTokenCookie));
        }

        function getIdToken() {
            return $cookies.get(getIdTokenCookieName(), globalCookieOptions);
        }

        function setTokenCookie(idToken) {
            const decodedToken = jwtHelper.decodeToken(idToken);
            const cookieOptions = {
                ...globalCookieOptions,
                expires: moment.unix(decodedToken.exp).toDate()
            };

            $cookies.put(getIdTokenCookieName(), idToken, cookieOptions);
        }

        function clearTokenCookie() {
            $cookies.put(getIdTokenCookieName(), '', globalCookieOptions);
            $cookies.remove(getIdTokenCookieName(), globalCookieOptions);
        }

        function getIdTokenCookieName() {
            return 'id_token';
        }

        function getAccessToken() {
            if (useUniversalLogin()) {
                return auth0SdkService.getTokenSilently().then((token) => {
                    return token;
                });
            }

            return $q.resolve(getIdToken());
        }

        function handleRedirectCallback() {
            return auth0SdkService.handleRedirectCallback();
        }

        return {
            handleRedirectCallback,
            login,
            logout,
            isLoggedIn,
            getShopperId,
            getAccessToken,
            useUniversalLogin
        };
    }).name;



// WEBPACK FOOTER //
// ./src/app/services/auth.service.js